import { useEffect, useState } from "react";
import { MapPin, MessageCircle, UserCog, User } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { SportPicker } from "@/components/physical-mapping/spot-review/SportPicker";
import type { ProposedStationRow } from "./ProposedStationsTable";
import {
  VERDICT_LABEL,
  VERDICT_STYLES,
  formatRelative,
} from "./ProposedStationsTable";

type PilRole = "main" | "backup" | "none";
type CityVerdict = "approve" | "reject" | "needs_change";

interface ProposedStationDrawerProps {
  collectionId: string;
  station: ProposedStationRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenLightbox: (initialIndex: number) => void;
}

export function ProposedStationDrawer({
  collectionId,
  station,
  open,
  onOpenChange,
  onOpenLightbox,
}: ProposedStationDrawerProps) {
  const utils = trpc.useUtils();
  const [notes, setNotes] = useState(station.admin_notes ?? "");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setNotes(station.admin_notes ?? "");
  }, [station.id, station.admin_notes]);

  const invalidate = () => {
    utils.physicalMapping.listApprovedStations.invalidate({ collectionId });
    utils.physicalMapping.getCityReviewSummary.invalidate({ collectionId });
  };

  const setRole = trpc.physicalMapping.setPilRole.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(e.message),
  });

  const setTier = trpc.physicalMapping.setAdminTier.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(e.message),
  });

  const setVerdict = trpc.physicalMapping.setCityVerdictByAdmin.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(e.message),
  });

  const setAdminNotesMutation = trpc.physicalMapping.setAdminNotes.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Notes saved");
    },
    onError: (e) => toast.error(e.message),
    onSettled: () => setSavingNotes(false),
  });

  const setLocker = trpc.physicalMapping.setLocker.useMutation({
    onSuccess: () => {
      invalidate();
      // Locker sports are denormalized onto the spot-review and location
      // detail views too — keep them fresh (see cache contract in router).
      utils.physicalMapping.listPILsForReview.invalidate();
      utils.physicalMapping.getLocationDetails.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const saveNotes = () => {
    if (notes === (station.admin_notes ?? "")) return;
    setSavingNotes(true);
    setAdminNotesMutation.mutate({
      pilId: station.id,
      notes: notes.length === 0 ? null : notes,
    });
  };

  const { data: allFeedback, isLoading: loadingComments } =
    trpc.physicalMapping.listCityFeedback.useQuery(
      { collectionId },
      { enabled: open },
    );
  const stationComments = (allFeedback ?? []).filter(
    (c: any) => c.pil_id === station.id,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {station.label}
            <Badge
              variant="outline"
              className={cn(
                "capitalize",
                station.role === "main" &&
                  "border-primary/30 bg-primary/10 text-primary",
              )}
            >
              {station.role}
            </Badge>
          </SheetTitle>
          {station.address && (
            <SheetDescription className="flex items-center gap-1 text-xs">
              <MapPin className="size-3" />
              {station.address}
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="p-4 space-y-5">
          {/* Photos */}
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Photos ({station.photos.length})
            </h4>
            {station.photos.length === 0 ? (
              <p className="text-xs text-muted-foreground">No photos.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {station.photos.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    className="aspect-square rounded overflow-hidden border hover:ring-2 hover:ring-primary"
                    onClick={() => onOpenLightbox(idx)}
                  >
                    <img
                      src={p.thumb_url}
                      alt=""
                      className="size-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Sports / lockers */}
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Lockers
            </h4>
            {station.lockers.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No lockers assigned.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {station.lockers.map((l) => (
                  <li key={l.locker_index} className="flex items-center gap-2">
                    <span className="w-16 shrink-0 text-xs text-muted-foreground">
                      Locker {l.locker_index}
                    </span>
                    <div className="flex-1 min-w-0">
                      <SportPicker
                        value={l.sport_type}
                        onChange={(sport) =>
                          setLocker.mutate({
                            pilId: station.id,
                            lockerIndex: l.locker_index,
                            sportType: sport,
                          })
                        }
                        locationSports={station.sports}
                        disabled={
                          setLocker.isPending &&
                          setLocker.variables?.lockerIndex === l.locker_index
                        }
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Assign the sport stored in each locker. Changes save immediately.
            </p>
          </section>

          {/* Editable fields */}
          <section className="space-y-4 rounded-lg border p-3 bg-muted/30">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Role
              </label>
              <ToggleGroup
                type="single"
                size="sm"
                variant="outline"
                value={station.role}
                onValueChange={(v) => {
                  if (!v) return;
                  setRole.mutate({
                    pilId: station.id,
                    role: v as PilRole,
                  });
                }}
                disabled={setRole.isPending}
                className="mt-1"
              >
                <ToggleGroupItem value="main">Main</ToggleGroupItem>
                <ToggleGroupItem value="backup">Backup</ToggleGroupItem>
                <ToggleGroupItem value="none">None</ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Admin tier
              </label>
              <ToggleGroup
                type="single"
                size="sm"
                variant="outline"
                value={station.admin_tier ? String(station.admin_tier) : ""}
                onValueChange={(v) => {
                  if (!v) return;
                  setTier.mutate({
                    pilId: station.id,
                    tier: Number(v) as 1 | 2 | 3,
                  });
                }}
                disabled={setTier.isPending}
                className="mt-1"
              >
                {([1, 2, 3] as const).map((t) => (
                  <ToggleGroupItem key={t} value={String(t)}>
                    Tier {t}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                City verdict
                {station.city_verdict_source === "admin" && (
                  <Badge
                    variant="outline"
                    className="text-[9px] font-normal px-1 py-0 gap-0.5"
                  >
                    <UserCog className="size-2.5" />
                    recorded by admin
                  </Badge>
                )}
              </label>
              <ToggleGroup
                type="single"
                size="sm"
                variant="outline"
                value={station.city_verdict ?? ""}
                onValueChange={(v) => {
                  setVerdict.mutate({
                    pilId: station.id,
                    verdict: (v as CityVerdict) || null,
                  });
                }}
                disabled={setVerdict.isPending}
                className="mt-1"
              >
                <ToggleGroupItem value="approve">Approve</ToggleGroupItem>
                <ToggleGroupItem value="needs_change">
                  Needs change
                </ToggleGroupItem>
                <ToggleGroupItem value="reject">Reject</ToggleGroupItem>
              </ToggleGroup>
              {station.city_verdict_at && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  recorded {formatRelative(station.city_verdict_at)}
                </p>
              )}
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Admin notes
              </label>
              <Textarea
                className="mt-1 min-h-[90px] text-sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={saveNotes}
                placeholder="Internal notes, not visible to the city…"
                maxLength={5000}
                disabled={savingNotes}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Saves on blur. {notes.length}/5000
              </p>
            </div>
          </section>

          {/* Meta */}
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Metadata
            </h4>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted-foreground">Placed by</dt>
              <dd className="capitalize flex items-center gap-1">
                <User className="size-3" />
                {station.placed_by ?? "—"}
              </dd>
              <dt className="text-muted-foreground">Created</dt>
              <dd>{formatRelative(station.created_at)}</dd>
              <dt className="text-muted-foreground">Approved</dt>
              <dd>{formatRelative(station.approved_at)}</dd>
            </dl>
          </section>

          {/* City comments */}
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
              <MessageCircle className="size-3" />
              City comments ({stationComments.length})
            </h4>
            {loadingComments ? (
              <Skeleton className="h-16 w-full" />
            ) : stationComments.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No comments from the city.
              </p>
            ) : (
              <ul className="space-y-2">
                {stationComments.map((c: any) => (
                  <li
                    key={c.id}
                    className={cn(
                      "rounded border p-2 text-sm",
                      !c.admin_seen && "bg-amber-50 border-amber-200",
                    )}
                  >
                    <div className="text-[10px] text-muted-foreground mb-1">
                      {formatRelative(c.created_at)}
                      {c.updated_at !== c.created_at && " · edited"}
                    </div>
                    <p className="whitespace-pre-wrap">{c.comment}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {station.city_verdict && (
            <Badge
              variant="outline"
              className={cn("text-xs", VERDICT_STYLES[station.city_verdict])}
            >
              {VERDICT_LABEL[station.city_verdict]}
            </Badge>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
